import React, { useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
} from "@mui/material";
import {
	ArrowBack as ArrowBackIcon,
	ArrowForward as ArrowForwardIcon,
	DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDrag, useDrop } from "react-dnd";

const warehousePillSx = {
	display: "inline-flex",
	alignItems: "center",
	gap: 1,
	px: 1.5,
	py: 1,
	borderRadius: "8px",
	cursor: "grab",
	transition: "box-shadow 0.2s, transform 0.2s",
	"&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.12)" },
	"&:active": { cursor: "grabbing" },
};

function DraggableWarehouse({ warehouse, index, zone }) {
	const [{ isDragging }, drag] = useDrag({
		type: "warehouse",
		item: { warehouse, zone, index },
		collect: (monitor) => ({ isDragging: monitor.isDragging() }),
	});

	const isFirst = index === 0;

	return (
		<Box
			ref={drag}
			sx={{
				...warehousePillSx,
				bgcolor: isFirst ? "primary.main" : "grey.100",
				color: isFirst ? "primary.contrastText" : "text.primary",
				opacity: isDragging ? 0.6 : 1,
				boxShadow: isDragging ? "0 4px 12px rgba(0,0,0,0.15)" : "0 1px 3px rgba(0,0,0,0.08)",
			}}
		>
			<DragIndicatorIcon sx={{ fontSize: 18, opacity: isFirst ? 0.9 : 0.6 }} />
			<Typography variant="body2" sx={{ fontWeight: 600 }}>{warehouse}</Typography>
			<Chip
				label={index + 1}
				size="small"
				sx={{
					height: 20,
					minWidth: 20,
					fontSize: "0.7rem",
					fontWeight: 700,
					bgcolor: isFirst ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.06)",
					color: "inherit",
				}}
			/>
		</Box>
	);
}


function WarehouseWithDropSlot({ warehouse, index, zone, warehouses, onReorder }) {
	const [{ isOver }, drop] = useDrop({
		accept: "warehouse",
		canDrop: (item) => item.zone === zone,
		drop: (item) => {
			if (item.zone !== zone) return;
			const fromIndex = item.index;
			const position = index;
			if (fromIndex === position) return;
			const newWarehouses = [...warehouses];
			const [removed] = newWarehouses.splice(fromIndex, 1);
			newWarehouses.splice(position, 0, removed);
			onReorder(zone, newWarehouses);
		},
		collect: (monitor) => ({ isOver: monitor.isOver() }),
	});

	return (
		<Box
			ref={drop}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				padding: "2px",
				borderRadius: "10px",
				border: "2px dashed transparent",
				borderColor: isOver ? "primary.main" : "transparent",
				bgcolor: isOver ? "action.hover" : "transparent",
				transition: "all 0.15s",
			}}
		>
			<DraggableWarehouse warehouse={warehouse} index={index} zone={zone} />
		</Box>
	);
}

function DroppableZone({ zone, warehouses, onReorder }) {
	const [{ isOver }, drop] = useDrop({
		accept: "warehouse",
		canDrop: () => false,
		collect: (monitor) => ({
			isOver: monitor.isOver(),
		}),
	});

	return (
		<Box
			ref={drop}
			sx={{
				p: 2,
				borderRadius: 2,
				border: "1px solid",
				borderColor: isOver ? "primary.main" : "grey.200",
				bgcolor: isOver ? "grey.100" : "grey.50",
				minHeight: 64,
				display: "flex",
				flexWrap: "wrap",
				alignItems: "center",
				gap: 0.5,
				transition: "all 0.2s",
			}}
		>
			{warehouses.map((warehouse, index) => (
				<WarehouseWithDropSlot
					key={`${warehouse}-${index}`}
					warehouse={warehouse}
					index={index}
					zone={zone}
					warehouses={warehouses}
					onReorder={onReorder}
				/>
			))}
			{warehouses.length === 0 && (
				<Typography variant="body2" color="text.secondary" sx={{ width: "100%", textAlign: "center" }}>
					Drag warehouses here
				</Typography>
			)}
		</Box>
	);
}

function ZoneMapping({ zoneMapping, regions, onChange, onNext, onBack, nextLoading = false }) {
	const [localMapping, setLocalMapping] = useState(zoneMapping);

	React.useEffect(() => {
		setLocalMapping(zoneMapping);
	}, [zoneMapping]);

	const handleReorder = (zone, newWarehouses) => {
		const updated = { ...localMapping };
		updated[zone] = newWarehouses;
		setLocalMapping(updated);
		onChange(updated);
	};

	const fileZones = regions.map((r) => r.toUpperCase());

	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Detected Regions and Zone Configuration
			</Typography>

			<Box sx={{ mb: 3 }}>
				<Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold", mb: 1 }}>
					Detected Regions ({regions.length}):
				</Typography>
				<Paper variant="outlined" sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
					{regions.length > 0 ? (
						regions.map((region) => (
							<Chip key={region} label={region} color="primary" sx={{ fontSize: "0.95rem", height: "32px", fontWeight: "bold" }} />
						))
					) : (
						<Typography color="text.secondary">No regions found</Typography>
					)}
				</Paper>
			</Box>

			<Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold", mb: 1 }}>
				Configure Warehouse Priority by Zone
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Drag warehouses horizontally to reorder priority. Priority 1 is highest priority.
			</Typography>

			<DndProvider backend={HTML5Backend}>
				<TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ bgcolor: "grey.100" }}>
								<TableCell sx={{ fontWeight: 600, width: 140, fontSize: "0.875rem" }}>Zone</TableCell>
								<TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>Warehouse Priority Order</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{fileZones.map((zone) => {
								const warehouses = localMapping[zone] || [];
								return (
									<TableRow key={zone} sx={{ "&:last-child td": { borderBottom: 0 } }}>
										<TableCell sx={{ fontWeight: 600, verticalAlign: "middle", width: 140, color: "text.secondary" }}>
											{zone}
										</TableCell>
										<TableCell sx={{ py: 1.5, verticalAlign: "middle" }}>
											<DroppableZone zone={zone} warehouses={warehouses} onReorder={handleReorder} />
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</DndProvider>

			<Box display="flex" justifyContent="space-between" mt={4}>
				<Button startIcon={<ArrowBackIcon />} onClick={onBack} variant="outlined">
					Back
				</Button>
				<Button
					endIcon={nextLoading ? null : <ArrowForwardIcon />}
					onClick={onNext}
					variant="contained"
					disabled={nextLoading}
					startIcon={nextLoading ? <CircularProgress size={18} color="inherit" /> : null}
				>
					{nextLoading ? "Saving…" : "Continue to Facility Selection"}
				</Button>
			</Box>
		</Box>
	);
}

export default ZoneMapping;
